/**
 * The business page chat's pure parts (customer agent, 10-21): what the customer is looking at, the visitor key the
 * server issued, and the in-chat login — phone → code → (a brand-new customer) name.
 */
import { productIdFromParam } from '../../utils/productUrl';

/** The product a `/p/<name>-<uuid>` address names, read by the storefront's one rule; none when it names no id. */
function productOfSegment(raw) {
  let segment = '';
  try { segment = decodeURIComponent(raw); } catch (_) { /* a broken escape names nothing */ }
  const productId = productIdFromParam(segment);
  return productId ? { page: 'product', productId } : { page: 'product' };
}

const SEGMENT_PAGES = [
  [/\/p\/([^/?#]+)/, (m) => productOfSegment(m[1])],
  [/\/shop\/c\/([^/?#]+)/, (m) => ({ page: 'category', categorySlug: decodeURIComponent(m[1]) })],
  [/\/shop(\/|$)/, () => ({ page: 'shop' })],
  [/\/checkout(\/|$)/, () => ({ page: 'checkout' })],
  [/\/add-appointment(\/|$)|\/booking(\/|$)/, () => ({ page: 'booking' })],
  [/\/orders\//, () => ({ page: 'order' })],
  [/\/appointments(\/|$)/, () => ({ page: 'appointments' })],
];

export function agentPageContext(location, extra = {}, cart = []) {
  const path = String(location?.pathname || '/');
  let ctx = { page: 'home' };
  for (const [re, make] of SEGMENT_PAGES) {
    const m = re.exec(path);
    if (m) { ctx = make(m); break; }
  }
  if (ctx.page === 'product' && extra.productName) ctx.productName = String(extra.productName).slice(0, 120);
  if (ctx.page === 'product' && extra.variantId) ctx.variantId = String(extra.variantId);
  // A product made the subject in the chat ("Ask about this", round 2) is the one the shopper is looking at — the
  // server reads page.productId before the turn and names it "this" / "it" (customer-agent.prompt ON SCREEN). It wins
  // over the product page behind the chat; that page's variant is not carried onto another product.
  // Only a product is a page the server reads; a service or a place for rent the chat asks about by its name (10-23).
  const focus = extra.focus && extra.focus.id && (!extra.focus.kind || extra.focus.kind === 'product') ? extra.focus : null;
  if (focus) {
    const id = String(focus.id);
    if (ctx.productId !== id) delete ctx.variantId;
    ctx.productId = id;
    if (focus.name) ctx.productName = String(focus.name).slice(0, 120);
    else delete ctx.productName;
  }
  const lines = (Array.isArray(cart) ? cart : []).filter((l) => l && l.productId).slice(0, 20)
    .map((l) => ({ productId: String(l.productId), ...(l.variantId ? { variantId: String(l.variantId) } : {}), quantity: Math.max(1, Number(l.quantity) || 1), ...(l.name ? { name: String(l.name).slice(0, 80) } : {}) }));
  if (lines.length) ctx.cart = lines;
  return ctx;
}

const KEY = (slug) => `agentVisitorKey:${slug}`;

export function readVisitorKey(slug) {
  try { return localStorage.getItem(KEY(slug)) || undefined; } catch (_) { return undefined; }
}

export function saveVisitorKey(slug, key) {
  try { if (key) localStorage.setItem(KEY(slug), key); } catch (_) { /* private mode: the chat just starts fresh */ }
}

/**
 * One step of the in-chat login. `say` is the key of the line the chat shows next; `token` is what to store
 * (a brand-new customer's onboarding token first, then their account token).
 */
const GENDERS = [
  ['Female', /^(f|female|woman|נקבה|אישה|אשה|أنثى|انثى|امرأة)$/i],
  ['Male', /^(m|male|man|זכר|גבר|ذكر|رجل)$/i],
  ['Other', /^(other|אחר|אחרת|آخر|اخر)$/i],
];
const SKIP = /^(skip|no|דלג|לא|تخطي|لا)$/i;
const genderOf = (text) => (GENDERS.find(([, re]) => re.test(String(text || '').trim())) || [null])[0];

/** The storefront's onboarding; a refusal is a step (gender asked) or a line — never a stalled chat (final review 10-21). */
async function onboard(phone, fullName, gender, api, opts) {
  let res;
  try {
    res = await api.onboardCustomer({ fullName, phone, ...(opts.salonId ? { salonId: opts.salonId } : {}), ...(gender ? { gender } : {}) });
  } catch (e) {
    if (e?.response?.data?.errorCode === 'GENDER_REQUIRED') return { state: { step: 'gender', phone, fullName }, say: 'askGender' };
    return { state: { step: 'name', phone }, say: 'tryAgain' };
  }
  const token = res?.accessToken || res?.token;
  return token ? { state: { step: 'done' }, token, say: 'signedIn', ...(res.customer ? { customer: res.customer } : {}) } : { state: { step: 'name', phone }, say: 'tryAgain' };
}

export async function loginStep(state, input, api, opts = {}) {
  const text = String(input ?? '').trim();
  if (state.step === 'phone') {
    let phone = null;
    try { phone = await api.normalizePhone(text); } catch (_) { phone = null; }
    if (!phone) return { state, say: 'needPhone' };
    await api.sendOtp(phone, 'CUSTOMER', opts.language);
    return { state: { step: 'code', phone }, say: 'codeSent' };
  }
  if (state.step === 'code') {
    const code = text.replace(/\D/g, '');
    if (code.length < 4) return { state, say: 'badCode' };
    let res;
    try {
      res = await api.verifyOtp(state.phone, code, { locale: opts.language });
    } catch (e) {
      const status = e?.response?.status;
      return { state, say: status === 400 || status === 422 ? 'badCode' : 'tryAgain' };
    }
    if (res?.needsOnboarding) {
      const onboarding = res.onboardingToken || res.token || res.accessToken;
      return { state: { step: 'name', phone: state.phone }, ...(onboarding ? { token: onboarding } : {}), say: 'askName' };
    }
    const token = res?.token || res?.accessToken;
    return token ? { state: { step: 'done' }, token, say: 'signedIn' } : { state, say: 'tryAgain' };
  }
  if (state.step === 'name') {
    if (text.length < 2) return { state, say: 'askName' };
    // A business that asks a new customer for their gender (settings.customerGenderAtSignup) asks it here (final review 10-21).
    if (opts.genderPolicy === 'required' || opts.genderPolicy === 'optional') return { state: { step: 'gender', phone: state.phone, fullName: text }, say: 'askGender' };
    return onboard(state.phone, text, undefined, api, opts);
  }
  if (state.step === 'gender') {
    const gender = genderOf(text);
    const skipped = opts.genderPolicy !== 'required' && SKIP.test(text);
    if (!gender && !skipped) return { state, say: 'askGender' };
    return onboard(state.phone, state.fullName, gender || undefined, api, opts);
  }
  return { state, say: 'tryAgain' };
}

/** A time of day in a choice's label ("10:00", "Tomorrow 9:15", "14:30–15:10"). */
const TIME = /(^|[^\d])([01]?\d|2[0-3])[:.][0-5]\d(?!\d)/;

/**
 * WHAT AN ANSWER IS ABOUT (round 3, owner 10-23): the small living character at its start. Only what the answer
 * carries decides it, never a guess from its words: products → `products`; a booking to approve (the customer
 * agent's proposals are bookings) or times to choose → `bookings`; a place, a plain answer, anything else → Luna
 * (`ai`).
 */
export function replyCharacter(message) {
  const m = message || {};
  // The chat's own lines say what they are about ("Added … to your cart" → products).
  if (m.character) return m.character;
  if (m.items?.products?.length) return 'products';
  if (m.items?.stays?.length) return 'stays';
  if (m.items?.services?.length || m.items?.appointments?.length) return 'bookings';
  if (m.items?.orders?.length) return 'products';
  if (m.confirmation?.proposalId) return 'bookings';
  const options = m.choices?.options || [];
  if (options.length && options.some((o) => TIME.test(String(o?.label ?? '')))) return 'bookings';
  return 'ai';
}

/**
 * THE CONVERSATION AS BLOCKS (owner 10-23, the portal's fb113f2f3): each of the customer's messages alone, a decision
 * on a card as a mark on their side, and the answers in a row — nothing from the customer between them — together, so
 * they read as one reply.
 */
export function groupMessages(messages) {
  const out = [];
  for (const m of messages || []) {
    const kind = m.role === 'assistant' ? 'assistant' : m.decision ? 'mark' : 'user';
    const last = out[out.length - 1];
    if (kind === 'assistant' && last?.kind === 'assistant') last.parts.push(m);
    else out.push({ kind, parts: [m] });
  }
  return out;
}
